import { Edit, useForm, useSelect } from "@refinedev/antd";
import { HttpError, useGo, useNavigation } from "@refinedev/core";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Row,
  Col,
  Space,
  Card,
  App,
  Table,
  Upload,
  List,
  Spin,
  Breadcrumb,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  InboxOutlined,
  CameraOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useSearchParams, useParams } from "react-router";
import type {
  ControlPointFromAuditFormValues,
  SampleInput,
  ControlPointDetail,
} from "../../types/control-point";
import { controlPointService, auditPlotService, photoService } from "../../services/api";
import type { PlotPhoto } from "../../types/audit";
import { ControlPointMapEditor } from "./components/ControlPointMapEditor";
import "./control-points.css";
import { usePageTitle } from "../../hooks";

const { TextArea } = Input;
const { Dragger } = Upload;

interface PhotoItem {
  uid: string;
  file: File;
  description: string;
  preview: string;
}

/**
 * ControlPointEditFull Component
 * Form for editing a control point in the audit where it was created
 * Allows editing all fields including geometry, samples, and photos
 */
export const ControlPointEditFull = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const go = useGo();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get("auditId");
  
  // Set page title
  usePageTitle(t("controlPoint.edit", "Editar Punto de Control"));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [referencePlots, setReferencePlots] = useState<GeoJSON.FeatureCollection | null>(null);
  const [samples, setSamples] = useState<SampleInput[]>([]);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  // Fotos que ya se habían subido en visitas anteriores al CP —
  // se cargan del backend en `loadData` y se pintan aparte del
  // dragger de fotos nuevas. El auditor puede eliminarlas
  // individualmente si se subieron por error. `PhotoItem` no
  // encaja para las existentes porque no tienen `File`.
  const [existingPhotos, setExistingPhotos] = useState<PlotPhoto[]>([]);
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [controlPoint, setControlPoint] = useState<ControlPointDetail | null>(null);

  const { formProps, form } = useForm<
    ControlPointFromAuditFormValues,
    HttpError,
    ControlPointFromAuditFormValues
  >({
    action: "create", // Use "create" to avoid automatic data loading
    redirect: false,
  });

  // Load reasons for select dropdown
  const { selectProps: reasonSelectProps } = useSelect({
    resource: "suspicious-crop-reasons",
    optionLabel: "name",
    optionValue: "id",
  });

  // Load control point data
  useEffect(() => {
    if (!id || !auditId) {
      message.error("No se especificó un punto de control o auditoría válidos");
      go({ to: "/audits" });
      return;
    }

    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);

        // Load control point
        const cpResponse = await controlPointService.getControlPoint(id);
        
        if (!isMounted) return;
        
        // Handle GeoJSON format - extract properties if needed
        let cpData: any = cpResponse;
        if (cpResponse && typeof cpResponse === 'object' && 'properties' in cpResponse) {
          // GeoJSON Feature format
          cpData = {
            ...cpResponse.properties,
            id: cpResponse.id || cpResponse.properties.id,
            geom: cpResponse.geometry,
          };
        }
        
        setControlPoint(cpData);
        
        // Set geometry. Puede venir como objeto (GeoJSON dict) o
        // como string serializado — parseamos para robustez, así el
        // `GeometryDisplay` reconoce el `.type` y pinta el polígono.
        if (cpData.geom) {
          const parsedGeom = typeof cpData.geom === 'string'
            ? JSON.parse(cpData.geom)
            : cpData.geom;
          setGeometry(parsedGeom);
        }

        // Get first review (the one from this audit)
        const firstReview = cpData.reviews?.find(r => r.is_first_review);
        
        if (firstReview) {
          // Set form values
          form.setFieldsValue({
            reason: cpData.reason,
            is_producer_owner: cpData.is_producer_owner,
            estimated_surface: firstReview.estimated_surface || 0,
            comment: firstReview.comment,
            geom: cpData.geom,
          });

          // Set samples
          if (firstReview.samples && firstReview.samples.length > 0) {
            setSamples(firstReview.samples.map((s: any) => ({
              code_sample: s.code || s.code_sample, // Use 'code' field which contains the lab code
              position_waypoint: s.position_waypoint || '',
            })));
          }
        }

        // Cargar fotos existentes del CP para que el auditor las vea
        // antes de subir nuevas — así puede confirmar cuáles ya subió
        // en visitas anteriores y evitar duplicados. Fallo silencioso
        // si el endpoint responde error: no bloquea la edición del CP.
        try {
          const cpPhotos = await photoService.getPhotosByControlPoint(id);
          if (isMounted) {
            const list = Array.isArray(cpPhotos)
              ? cpPhotos
              : ((cpPhotos as any)?.results ?? (cpPhotos as any)?.data ?? []);
            setExistingPhotos(list);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[cp-edit] No se pudieron cargar las fotos existentes', err);
        }

        // Load audited plots for reference
        try {
          const plotsResponse = await auditPlotService.getPlotsByAudit(Number(auditId));
          
          if (!isMounted) return;
          
          let plots: any[] = [];
          if (plotsResponse && typeof plotsResponse === 'object' && 'features' in plotsResponse) {
            plots = (plotsResponse as any).features.map((feature: any) => ({
              id: feature.id || feature.properties.id,
              ...feature.properties,
              geom: feature.geometry,
            }));
          } else if (Array.isArray(plotsResponse)) {
            plots = plotsResponse;
          }
          
          const features = plots
            .filter((plot: any) => plot.geom)
            .map((plot: any) => ({
              type: "Feature" as const,
              geometry: typeof plot.geom === 'string' ? JSON.parse(plot.geom) : plot.geom,
              properties: {
                name: plot.name,
                surface: plot.surface,
              },
            }));
          
          if (features.length > 0) {
            setReferencePlots({
              type: "FeatureCollection",
              features,
            });
          }
        } catch (error) {
          console.error("Error loading audited plots:", error);
        }
      } catch (error: any) {
        console.error("Error loading control point:", error);
        message.error(error.message || "Error al cargar el punto de control");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [id, auditId]);

  /**
   * Update form when geometry changes
   */
  useEffect(() => {
    form.setFieldValue("geom", geometry);
  }, [geometry, form]);

  /**
   * Add a new sample row
   */
  const handleAddSample = () => {
    setSamples([...samples, { code_sample: "", position_waypoint: "" }]);
  };

  /**
   * Remove a sample row
   */
  const handleRemoveSample = (index: number) => {
    setSamples(samples.filter((_, i) => i !== index));
  };

  /**
   * Update a sample field
   */
  const handleSampleChange = (index: number, field: keyof SampleInput, value: string) => {
    const newSamples = [...samples];
    newSamples[index] = { ...newSamples[index], [field]: value };
    setSamples(newSamples);
  };

  /**
   * Validate image file type and size
   */
  const validateFile = (file: File): boolean => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      message.error("Solo se permiten archivos JPG, JPEG y PNG");
      return false;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error("El tamaño del archivo no puede exceder 10MB");
      return false;
    }

    return true;
  };

  /**
   * Handle file selection from Upload component
   */
  const handleFileChange = (info: any) => {
    const { fileList } = info;
    const newPhotos: PhotoItem[] = [];

    fileList.forEach((file: UploadFile) => {
      if (file.originFileObj) {
        if (!validateFile(file.originFileObj)) {
          return;
        }

        const preview = URL.createObjectURL(file.originFileObj);

        newPhotos.push({
          uid: file.uid,
          file: file.originFileObj as File,
          description: "",
          preview,
        });
      }
    });

    setPhotos(newPhotos);
  };

  /**
   * Handle description change for a photo
   */
  const handleDescriptionChange = (uid: string, description: string) => {
    setPhotos((prevPhotos) =>
      prevPhotos.map((photo) =>
        photo.uid === uid ? { ...photo, description } : photo
      )
    );
  };

  /**
   * Elimina una foto ya persistida en el backend. Confirma con
   * modal (evita clicks accidentales) y refresca el listado. Fallo
   * silencioso a nivel toast — el modal.error lo captura desde
   * el try/catch del componente padre.
   */
  const handleDeleteExistingPhoto = async (photoId: string) => {
    try {
      setDeletingPhotoId(photoId);
      await photoService.deletePhoto(photoId);
      setExistingPhotos((prev) => prev.filter((p) => p.id !== photoId));
      message.success(t('photo.deleted', 'Foto eliminada.'));
    } catch (err: any) {
      message.error(
        err?.originalError?.response?.data?.detail
        ?? err?.message
        ?? t('photo.delete_error', 'No se pudo eliminar la foto.'),
      );
    } finally {
      setDeletingPhotoId(null);
    }
  };

  /**
   * Remove a photo from the list
   */
  const handleRemovePhoto = (uid: string) => {
    setPhotos((prevPhotos) => {
      const photoToRemove = prevPhotos.find((p) => p.uid === uid);
      if (photoToRemove) {
        URL.revokeObjectURL(photoToRemove.preview);
      }
      return prevPhotos.filter((p) => p.uid !== uid);
    });
  };

  /**
   * Convert file to base64
   */
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: ControlPointFromAuditFormValues) => {
    if (!id || !auditId) {
      message.error("No se especificó un punto de control o auditoría válidos");
      return;
    }

    try {
      setIsSubmitting(true);

      // Update control point basic fields
      await controlPointService.updateControlPoint(id, {
        reason: values.reason,
        is_producer_owner: values.is_producer_owner,
        geom: geometry || undefined,
      });

      // Update first review
      const firstReview = controlPoint?.reviews?.find(r => r.is_first_review);
      if (firstReview) {
        await controlPointService.updateReview(firstReview.id, {
          comment: values.comment,
          estimated_surface: values.estimated_surface || 0,
        });

        // Update samples
        const validSamples = samples.filter(s => s.code_sample.trim() !== '');
        if (validSamples.length > 0) {
          await controlPointService.updateReviewSamples(firstReview.id, validSamples);
        }
      }

      // Upload new photos if any
      if (photos.length > 0 && firstReview) {
        try {
          const { apiClient } = await import("../../services/api/apiClient");
          
          for (const photo of photos) {
            const base64Image = await fileToBase64(photo.file);
            
            // Create photo via API
            await apiClient.post('/plot-photos/', {
              audit: Number(auditId),
              control_point_review: firstReview.id,
              image_base64: base64Image,
              description: photo.description || '',
            });
          }
          
          console.log(`${photos.length} foto(s) subida(s) exitosamente`);
        } catch (error: any) {
          console.error("Error uploading photos:", error);
          message.warning("Algunas fotos no pudieron ser subidas");
        }
      }

      message.success("Punto de control actualizado exitosamente");

      // Clean up preview URLs
      photos.forEach((photo) => {
        URL.revokeObjectURL(photo.preview);
      });

      // Navigate back to audit
      go({ to: `/audits/${auditId}` });
    } catch (error: any) {
      message.error(
        error.message || "Error al actualizar el punto de control"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!controlPoint) {
    return null;
  }

  // Sample table columns
  const sampleColumns = [
    {
      title: "Código de Laboratorio",
      dataIndex: "code_sample",
      key: "code_sample",
      render: (_: any, sample: SampleInput, index: number) => (
        <Input
          value={sample.code_sample}
          onChange={(e) => handleSampleChange(index, 'code_sample', e.target.value)}
          placeholder="Ej: LAB001"
        />
      ),
    },
    {
      title: "Número Waypoint",
      dataIndex: "position_waypoint",
      key: "position_waypoint",
      render: (_: any, sample: SampleInput, index: number) => (
        <Input
          value={sample.position_waypoint}
          onChange={(e) => handleSampleChange(index, 'position_waypoint', e.target.value)}
          placeholder="Ej: WP001"
        />
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      render: (_: any, __: SampleInput, index: number) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveSample(index)}
        />
      ),
    },
  ];

  return (
    <Edit
      title={`${t("controlPoint.edit", "Editar Punto de Control")} - ${controlPoint.name}`}
      breadcrumb={
        <Breadcrumb
          items={[
            {
              title: "Auditorías",
              href: "/audits",
            },
            {
              title: auditId ? `Auditoría #${auditId}` : "Auditoría",
              href: auditId ? `/audits/${auditId}` : "/audits",
            },
            {
              title: "Editar Punto de Control",
            },
          ]}
        />
      }
      saveButtonProps={{
        loading: isSubmitting,
        onClick: () => {
          form.submit();
        },
      }}
      headerButtons={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            auditId ? go({ to: `/audits/${auditId}` }) : go({ to: "/control-points" })
          }
        >
          {t("common.back", "Volver")}
        </Button>
      }
      footerButtons={
        <Space>
          <Button
            onClick={() =>
              auditId
                ? go({ to: `/audits/${auditId}` })
                : go({ to: "/control-points" })
            }
          >
            {t("common.cancel", "Cancelar")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={isSubmitting}
            onClick={() => form.submit()}
          >
            {t("common.save", "Guardar")}
          </Button>
        </Space>
      }
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Card
          title={t("controlPoint.basicInfo", "Información Básica")}
          style={{ marginBottom: 24 }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t("controlPoint.reason", "Motivo")}
                name="reason"
                rules={[
                  {
                    required: true,
                    message: `${t("controlPoint.reason")} ${t("common.required")}`,
                  },
                ]}
              >
                <Select
                  {...reasonSelectProps}
                  placeholder={t(
                    "controlPoint.reasonPlaceholder",
                    "Seleccione el motivo"
                  )}
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Form.Item
                label={t(
                  "controlPoint.isProducerOwner",
                  "¿Pertenece al Productor?"
                )}
                name="is_producer_owner"
                valuePropName="checked"
                tooltip={t(
                  "controlPoint.isProducerOwnerTooltip",
                  "Indica si el cultivo sospechoso pertenece al productor auditado"
                )}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                label={t("controlPoint.estimatedSurface", "Superficie Estimada (ha)")}
                name="estimated_surface"
                tooltip={t(
                  "controlPoint.estimatedSurfaceTooltip",
                  "Superficie estimada del cultivo sospechoso en hectáreas"
                )}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  placeholder="Ej: 2.5"
                  min={0}
                  step={0.1}
                  precision={2}
                  addonAfter="ha"
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title={
            <Space>
              <EnvironmentOutlined />
              {t("controlPoint.location", "Ubicación")}
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <p style={{ marginBottom: 16, color: "#666" }}>
            {t(
              "controlPoint.locationNote",
              "Marque un punto o dibuje un polígono en el mapa para indicar la ubicación del cultivo sospechoso."
            )}
          </p>
          
          <div 
            style={{ 
              height: "500px", 
              width: "100%",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              overflow: "hidden",
              position: "relative"
            }}
          >
            <ControlPointMapEditor
              geometry={geometry}
              onGeometryChange={setGeometry}
              editable={true}
              referencePlots={referencePlots}
            />
          </div>
          
          {geometry && (
            <div style={{ marginTop: "8px", fontSize: "12px", color: "#52c41a" }}>
              ✓ {geometry.type === "Point" ? "Punto marcado" : "Polígono dibujado"}
            </div>
          )}

          <Form.Item name="geom" hidden>
            <Input />
          </Form.Item>
        </Card>

        <Card
          title={t("controlPoint.initialReview", "Revisión Inicial")}
          style={{ marginBottom: 24 }}
        >
          <Form.Item
            label={t("controlPoint.comment", "Comentario")}
            name="comment"
            rules={[
              {
                required: true,
                message: `${t("controlPoint.comment")} ${t("common.required")}`,
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t(
                "controlPoint.commentPlaceholder",
                "Describa el cultivo sospechoso detectado..."
              )}
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </Card>

        <Card
          title={t("controlPoint.samples", "Muestras")}
          style={{ marginBottom: 24 }}
        >
          <p style={{ marginBottom: 16, color: "#666" }}>
            {t(
              "controlPoint.samplesNote",
              "Muestras asociadas a este punto de control."
            )}
          </p>

          <Button
            type="dashed"
            onClick={handleAddSample}
            icon={<PlusOutlined />}
            style={{ marginBottom: 16, width: "100%" }}
          >
            {t("controlPoint.addSample", "Añadir Muestra")}
          </Button>

          {samples.length > 0 && (
            <Table
              dataSource={samples}
              columns={sampleColumns}
              pagination={false}
              rowKey={(_, index) => `sample-${index}`}
              size="small"
            />
          )}
        </Card>

        <Card
          title={
            <Space>
              <CameraOutlined />
              {t("controlPoint.photos", "Fotografías")}
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <p style={{ marginBottom: 16, color: "#666" }}>
            {t(
              "controlPoint.photosNote",
              "Añada nuevas fotografías del cultivo sospechoso."
            )}
          </p>

          {/* Fotos existentes — se cargan al montar la página y se
              muestran encima del dragger para que el auditor sepa
              qué material ya se ha subido en visitas anteriores. */}
          {existingPhotos.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>
                {t('controlPoint.existing_photos', 'Fotos ya subidas')} ({existingPhotos.length})
              </div>
              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 3 }}
                dataSource={existingPhotos}
                renderItem={(ph) => (
                  <List.Item>
                    <Card
                      size="small"
                      cover={
                        <div
                          style={{
                            height: 200,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f0f0f0',
                          }}
                        >
                          <img
                            alt={ph.description || 'foto'}
                            src={ph.image_url}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </div>
                      }
                      actions={[
                        <Popconfirm
                          key="del"
                          title={t('photo.delete_confirm', '¿Eliminar esta foto?')}
                          okText={t('common.delete', 'Eliminar')}
                          cancelText={t('common.cancel', 'Cancelar')}
                          okButtonProps={{ danger: true }}
                          onConfirm={() => handleDeleteExistingPhoto(ph.id)}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deletingPhotoId === ph.id}
                          >
                            {t('common.delete', 'Eliminar')}
                          </Button>
                        </Popconfirm>,
                      ]}
                    >
                      <Card.Meta
                        title={
                          <div style={{ fontSize: 12, color: '#666' }}>
                            {ph.uploaded_by_name || '—'}
                          </div>
                        }
                        description={
                          <div style={{ fontSize: 12 }}>
                            {ph.description || (
                              <span style={{ color: '#999' }}>
                                {t('photo.no_description', 'Sin descripción')}
                              </span>
                            )}
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          )}

          {photos.length === 0 && (
            <Dragger
              multiple
              accept="image/jpeg,image/jpg,image/png"
              beforeUpload={() => false}
              onChange={handleFileChange}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t("photo.drag_drop") ||
                  "Haga clic o arrastre archivos a esta área para cargar"}
              </p>
              <p className="ant-upload-hint">
                {t("photo.upload_hint") ||
                  "Soporta carga múltiple. Solo archivos JPG, JPEG y PNG. Tamaño máximo: 10MB por archivo."}
              </p>
            </Dragger>
          )}

          {photos.length > 0 && (
            <>
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <span>
                    {t("photo.selected_photos") || "Fotos seleccionadas"}:{" "}
                    <strong>{photos.length}</strong>
                  </span>
                  <Button
                    size="small"
                    onClick={() => {
                      photos.forEach((photo) => {
                        URL.revokeObjectURL(photo.preview);
                      });
                      setPhotos([]);
                    }}
                  >
                    {t("photo.clear_all") || "Limpiar todo"}
                  </Button>
                </Space>
              </div>

              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 3, xl: 3 }}
                dataSource={photos}
                renderItem={(photo) => (
                  <List.Item>
                    <Card
                      size="small"
                      cover={
                        <div
                          style={{
                            height: 200,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#f0f0f0",
                          }}
                        >
                          <img
                            alt={photo.file.name}
                            src={photo.preview}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      }
                      actions={[
                        <Button
                          key="delete"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemovePhoto(photo.uid)}
                        >
                          {t("common.delete") || "Eliminar"}
                        </Button>,
                      ]}
                    >
                      <Card.Meta
                        title={
                          <div
                            style={{
                              fontSize: 12,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={photo.file.name}
                          >
                            {photo.file.name}
                          </div>
                        }
                        description={
                          <div>
                            <label style={{ fontSize: 12, fontWeight: 500 }}>
                              {t("photo.description") ||
                                "Descripción (opcional)"}
                            </label>
                            <TextArea
                              rows={2}
                              placeholder={
                                t("photo.description_placeholder") ||
                                "Agregar descripción..."
                              }
                              value={photo.description}
                              onChange={(e) =>
                                handleDescriptionChange(
                                  photo.uid,
                                  e.target.value
                                )
                              }
                              maxLength={500}
                            />
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            </>
          )}
        </Card>
      </Form>
    </Edit>
  );
};
